
// Libraries
import { PropType, defineComponent } from 'vue';

// Components
import MfaChooser from 'client/containers/user/MfaComponents/MfaChooser.vue';

// Constants
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Types
import type { MfaFactor } from 'client/types/Auth';

export default defineComponent({
  name: 'MfaView',

  components: {
    MfaChooser,
  },

  props: {
    factors: {
      type: Array as PropType<MfaFactor[]>,
      required: true,
    },
    mfaType: {
      type: String,
      required: true,
    },
    shouldHideEmailBadge: Boolean,
    /** When true, hide "choose a different way" / other factors (e.g. after user chose JC GO) */
    hideAlternateFactors: Boolean,
  },

  setup() {
    const uiLabel = useMfaLabels();
    return { uiLabel };
  },

  computed: {
    /** Alternate factors excluding the current MFA handler
     * (still empty chosen when on aggregate picker). */
    alternateFactorsExcludingChosen() {
      return this.factors.filter((factor) => factor.name !== this.mfaType);
    },

    /** SMS should never be treated as previously last chosen - always defer to backup */
    smsWasPreviouslyLastChosen(): boolean {
      return false;
    },

    /** When true, defer sms_otp behind "Choose A Different Way" on empty selection. */
    hasDeferrableBackupOnBlankPicker(): boolean {
      if (this.hasSelectedFactor || this.smsWasPreviouslyLastChosen) {
        return false;
      }
      const deferNames = new Set(MfaConstants.mfaChooserDeferredBackupNames);
      const pool = this.alternateFactorsExcludingChosen;
      return pool.some((f) => deferNames.has(f.name))
        && pool.some((f) => !deferNames.has(f.name));
    },

    /** Primary MFA rows (alternate list merges when on a chosen factor UI). */
    chooserPrimaryFactors() {
      if (!this.hasDeferrableBackupOnBlankPicker) {
        return this.alternateFactorsExcludingChosen;
      }
      const deferNames = new Set(MfaConstants.mfaChooserDeferredBackupNames);
      return this.alternateFactorsExcludingChosen.filter((f) => !deferNames.has(f.name));
    },

    /** Backup MFA rows until user taps "Choose A Different Way". */
    chooserDeferredBackupFactors() {
      if (!this.hasDeferrableBackupOnBlankPicker) {
        return [];
      }
      const deferNames = new Set(MfaConstants.mfaChooserDeferredBackupNames);
      return this.alternateFactorsExcludingChosen.filter((f) => deferNames.has(f.name));
    },

    /** Any factor other than the one currently routed (SMS may already be stripped from props). */
    hasAlternateFactorsAvailable() {
      return this.alternateFactorsExcludingChosen.length > 0;
    },

    showAlternateFactors() {
      return this.hasAlternateFactorsAvailable && !this.hideAlternateFactors;
    },

    hasSelectedFactor() {
      return !!this.mfaType;
    },

    shouldShowDivider() {
      return this.showAlternateFactors && this.hasSelectedFactor;
    },

    message() {
      return this.uiLabel.chooser.help;
    },
  },

  methods: {
    handleChangeFactor(factorName: string) {
      const factor = this.factors.find((f) => factorName === f.name);

      if (!factor) {
        return;
      }

      const route = {
        ...factor.route,
        params: {
          ...factor.route.params,
          from: this.$route.query.mfaType,
        },
      };

      this.$router.replace(route);

      // Clear any error that may have occurred in a different factor
      this.$emit('resetErrorMessage');
    },
  },
});
